import { Link } from 'react-router-dom';
import {
  ArrowRightIcon,
  BriefcaseIcon,
  CheckBadgeIcon,
  DocumentCheckIcon,
  LightBulbIcon,
  ShieldCheckIcon,
  SparklesIcon,
  UserGroupIcon,
  AcademicCapIcon,
} from '@heroicons/react/24/outline';

import interviewImage from '../../assets/landing/pipeline-interview.jpg';
import skillsImage from '../../assets/landing/pipeline-skills.jpg';
import guidanceImage from '../../assets/landing/pipeline-guidance.jpg';
import expertImage from '../../assets/landing/pipeline-expert.jpg';

const pipeline = [
  {
    title: 'Tell your story',
    eyebrow: 'Structured interview',
    description:
      'A guided, AI-assisted interview helps students explain their experiences naturally—not reduce them to a form or a score.',
    image: interviewImage,
    alt: 'A student taking part in a guided online interview from her laptop',
  },
  {
    title: 'Make skills visible',
    eyebrow: 'Evidence-led analysis',
    description:
      'Smash connects answers and supporting evidence to demonstrated strengths, relevant skills, and areas where more development may help.',
    image: skillsImage,
    alt: 'A student considering a visual map of skills, strengths, and development needs',
  },
  {
    title: 'Turn insight into direction',
    eyebrow: 'AI-assisted guidance',
    description:
      'Personalized feedback turns interview insight into practical next steps, learning priorities, and mentoring conversations.',
    image: guidanceImage,
    alt: 'A student choosing the next step on a personalized learning and development path',
  },
  {
    title: 'Keep people in control',
    eyebrow: 'Human expert review',
    description:
      'A real expert checks the evidence, tunes the evaluation, and approves the result before it becomes part of the student’s trusted profile.',
    image: expertImage,
    alt: 'A human expert reviewing an AI-assisted evaluation together with a student',
  },
];

const audiences = [
  {
    icon: SparklesIcon,
    title: 'For students',
    description:
      'Build a living portfolio from interviews, projects, learning, and verified evidence—then see the skills behind your progress.',
  },
  {
    icon: BriefcaseIcon,
    title: 'For organizations',
    description:
      'Run consistent interviews, understand candidate potential, and make decisions using traceable evidence instead of isolated impressions.',
  },
  {
    icon: UserGroupIcon,
    title: 'For human experts',
    description:
      'Review transcripts and evidence, add professional context, request improved evaluations, and approve the final outcome.',
  },
];

const features = [
  {
    icon: DocumentCheckIcon,
    title: 'Evidence-backed portfolios',
    description: 'Connect projects, certificates, activities, and interviews to a student’s developing profile.',
  },
  {
    icon: LightBulbIcon,
    title: 'Useful development insight',
    description: 'Translate demonstrated skills and needs into clear feedback that supports the next conversation.',
  },
  {
    icon: ShieldCheckIcon,
    title: 'Responsible by design',
    description: 'Keep AI drafts private until a qualified person reviews and approves the evaluation.',
  },
];

export default function LandingPage() {
  return (
    <main className="overflow-hidden bg-white text-slate-950 dark:bg-slate-950 dark:text-white">
      <section className="relative isolate border-b border-slate-200 dark:border-slate-800">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(circle_at_top_left,_rgba(44,89,248,0.17),_transparent_42%),radial-gradient(circle_at_85%_35%,_rgba(20,184,166,0.13),_transparent_32%)]" />
        <div className="mx-auto grid min-h-[640px] max-w-7xl items-center gap-14 px-6 py-20 lg:grid-cols-[1.08fr_0.92fr] lg:px-8 lg:py-24">
          <div className="max-w-3xl">
            <p className="mb-5 inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-800 dark:border-blue-800 dark:bg-blue-950/70 dark:text-blue-200">
              <SparklesIcon className="h-4 w-4" aria-hidden="true" />
              AI insight. Human judgment. Real growth.
            </p>
            <h1 className="font-poppins text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl lg:leading-[1.08]">
              See the person behind every answer.
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-8 text-slate-600 dark:text-slate-300">
              Smash turns structured interviews and real-world evidence into a clearer picture of a person’s skills, needs, and potential—with human experts in control of every final evaluation.
            </p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <Link to="/register" className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-700 px-6 py-3.5 font-semibold text-white shadow-lg shadow-blue-700/20 transition hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:bg-blue-600 dark:hover:bg-blue-500 dark:focus:ring-offset-slate-950">
                Create your portfolio <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
              </Link>
              <Link to="/login" className="inline-flex items-center justify-center rounded-xl border border-slate-300 bg-white px-6 py-3.5 font-semibold text-slate-800 transition hover:border-blue-300 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:border-slate-700 dark:bg-slate-900 dark:text-white dark:hover:bg-slate-800 dark:focus:ring-offset-slate-950">
                Log in
              </Link>
            </div>
            <p className="mt-5 text-sm text-slate-500 dark:text-slate-400">Students can create an account. Organization and expert access is invitation-based.</p>
          </div>

          <div className="relative mx-auto w-full max-w-xl" aria-label="Smash evaluation principles">
            <div className="absolute -inset-5 -z-10 rounded-[2.5rem] bg-gradient-to-br from-blue-500/20 via-cyan-300/10 to-orange-300/20 blur-2xl" />
            <div className="rounded-[2rem] border border-white/70 bg-white/90 p-6 shadow-2xl shadow-blue-950/10 backdrop-blur dark:border-slate-700 dark:bg-slate-900/90">
              <div className="flex items-center justify-between border-b border-slate-200 pb-5 dark:border-slate-700">
                <div>
                  <p className="text-sm font-semibold text-blue-700 dark:text-blue-300">A trusted feedback loop</p>
                  <p className="mt-1 text-xl font-bold">From voice to verified insight</p>
                </div>
                <CheckBadgeIcon className="h-10 w-10 text-teal-600" aria-hidden="true" />
              </div>
              <ol className="mt-6 space-y-4">
                {['Interview and evidence', 'Skills and development needs', 'Personalized guidance', 'Expert-reviewed outcome'].map((label, index) => (
                  <li key={label} className="flex items-center gap-4 rounded-2xl bg-slate-50 p-4 dark:bg-slate-800/80">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-700 font-bold text-white">{index + 1}</span>
                    <span className="font-semibold">{label}</span>
                    {index === 3 && <span className="ml-auto rounded-full bg-teal-100 px-2.5 py-1 text-xs font-bold text-teal-800 dark:bg-teal-950 dark:text-teal-200">Human approved</span>}
                  </li>
                ))}
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section id="platform" className="scroll-mt-24 px-6 py-20 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-blue-700 dark:text-blue-300">Built around people</p>
            <h2 className="mt-3 font-poppins text-3xl font-bold sm:text-4xl">One shared picture of growth</h2>
            <p className="mt-4 text-lg text-slate-600 dark:text-slate-300">Students own their story, organizations get structured insight, and experts bring the judgment that responsible evaluation requires.</p>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {audiences.map(({ icon: Icon, title, description }) => (
              <article key={title} className="rounded-3xl border border-slate-200 bg-slate-50 p-7 transition hover:-translate-y-1 hover:shadow-xl dark:border-slate-800 dark:bg-slate-900 motion-reduce:transform-none">
                <span className="inline-flex rounded-2xl bg-blue-100 p-3 text-blue-700 dark:bg-blue-950 dark:text-blue-300"><Icon className="h-6 w-6" aria-hidden="true" /></span>
                <h3 className="mt-5 text-xl font-bold">{title}</h3>
                <p className="mt-3 leading-7 text-slate-600 dark:text-slate-300">{description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section id="journey" className="scroll-mt-20 bg-slate-50 px-6 py-20 dark:bg-slate-900/60 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-3xl">
            <p className="text-sm font-bold uppercase tracking-[0.2em] text-blue-700 dark:text-blue-300">How it works</p>
            <h2 className="mt-3 font-poppins text-3xl font-bold sm:text-4xl">Technology moves the work forward. People make it trustworthy.</h2>
            <p className="mt-4 text-lg leading-8 text-slate-600 dark:text-slate-300">AI helps organize complex interview evidence and prepare useful feedback. It does not replace the student’s voice or the expert’s professional judgment.</p>
          </div>
          <ol className="relative mt-14 grid gap-8 lg:grid-cols-2" aria-label="Smash interview and evaluation journey">
            {pipeline.map((step, index) => (
              <li key={step.title} className="group overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
                <div className="aspect-[4/3] overflow-hidden bg-blue-50 dark:bg-slate-800">
                  <img src={step.image} alt={step.alt} loading="lazy" width="1448" height="1086" className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.02] motion-reduce:transform-none" />
                </div>
                <div className="relative p-7 sm:p-8">
                  <span className="absolute -top-7 right-7 flex h-14 w-14 items-center justify-center rounded-2xl border-4 border-white bg-blue-700 text-xl font-bold text-white shadow dark:border-slate-900">{index + 1}</span>
                  <p className="text-sm font-bold uppercase tracking-wider text-blue-700 dark:text-blue-300">{step.eyebrow}</p>
                  <h3 className="mt-2 text-2xl font-bold">{step.title}</h3>
                  <p className="mt-3 leading-7 text-slate-600 dark:text-slate-300">{step.description}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section id="trust" className="scroll-mt-20 px-6 py-20 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="grid gap-10 lg:grid-cols-[0.85fr_1.15fr] lg:items-center">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.2em] text-blue-700 dark:text-blue-300">Human-in-the-loop</p>
              <h2 className="mt-3 font-poppins text-3xl font-bold sm:text-4xl">Insight you can examine—not a black-box verdict.</h2>
              <p className="mt-5 text-lg leading-8 text-slate-600 dark:text-slate-300">Experts can see the interview, supporting excerpts, and AI rationale. They can annotate answers, request a revised evaluation, and approve only the result they stand behind.</p>
            </div>
            <div className="grid gap-5 sm:grid-cols-3">
              {features.map(({ icon: Icon, title, description }) => (
                <article key={title} className="rounded-2xl border border-slate-200 p-6 dark:border-slate-800">
                  <Icon className="h-7 w-7 text-teal-600" aria-hidden="true" />
                  <h3 className="mt-4 font-bold">{title}</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">{description}</p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 pb-20 lg:px-8">
        <div className="mx-auto grid max-w-7xl items-center gap-8 overflow-hidden rounded-[2rem] border border-blue-200 bg-gradient-to-br from-blue-50 to-teal-50 p-8 dark:border-blue-900 dark:from-blue-950/50 dark:to-teal-950/30 md:grid-cols-[1fr_auto] md:p-12">
          <div>
            <span className="inline-flex rounded-2xl bg-blue-700 p-3 text-white"><AcademicCapIcon className="h-7 w-7" aria-hidden="true" /></span>
            <p className="mt-5 text-sm font-bold uppercase tracking-[0.18em] text-blue-700 dark:text-blue-300">Smash Index for IB schools</p>
            <h2 className="mt-2 font-poppins text-3xl font-bold">Make ATL growth and CAS evidence easier to understand.</h2>
            <p className="mt-4 max-w-3xl leading-7 text-slate-600 dark:text-slate-300">Connect structured interviews, reflections, activities, and supporting evidence. AI helps identify skill and development signals while advisors and human experts retain control over what is approved.</p>
            <div className="mt-5 flex flex-wrap gap-2">{['ATL skill visibility', 'CAS evidence and reflection', 'AI-assisted analysis', 'Human validation'].map((item) => <span key={item} className="rounded-full bg-white px-3 py-1.5 text-sm font-semibold text-slate-700 shadow-sm dark:bg-slate-900 dark:text-slate-200">{item}</span>)}</div>
          </div>
          <Link to="/ib-schools" className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-700 px-6 py-3.5 font-semibold text-white shadow-lg shadow-blue-700/20 hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-slate-950">Explore IB workflows <ArrowRightIcon className="h-4 w-4" aria-hidden="true" /></Link>
        </div>
      </section>

      <section className="px-6 pb-20 lg:px-8">
        <div className="mx-auto max-w-7xl overflow-hidden rounded-[2rem] bg-blue-950 px-6 py-14 text-center text-white shadow-2xl sm:px-12">
          <h2 className="font-poppins text-3xl font-bold sm:text-4xl">Make every experience count.</h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-blue-100">Start a portfolio that grows with your interviews, evidence, learning, and achievements.</p>
          <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
            <Link to="/register" className="rounded-xl bg-white px-6 py-3.5 font-semibold text-blue-950 transition hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-950">Create an account</Link>
            <Link to="/login" className="rounded-xl border border-blue-700 px-6 py-3.5 font-semibold text-white transition hover:bg-blue-900 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-blue-950">Log in</Link>
          </div>
        </div>
      </section>
    </main>
  );
}
